import React, { useCallback, useEffect, useSyncExternalStore } from 'react';
import { TypedMemo } from '../../../common/types';
import { useMusic24Plan } from '../../hooks/useMusic24Plan';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { useIsUpsellMember } from '../useIsUpsellMember';
import { useLowestPlanPrice } from '../useLowestPlanPrice';
import { useOpenWatermarkOffer } from '../useOpenWatermarkOffer';
import { useUpsellEventFacts } from '../useUpsellEventFacts';
import { useWatermarkSummary } from '../useWatermarkSummary';
import { trackUpsellContinued, trackUpsellDismissed, trackUpsellShown } from '../watermarkUpsellAnalytics';
import { WatermarkChoiceModal } from './WatermarkChoiceModal';
import { closeWatermarkChoice, getWatermarkChoice, subscribeWatermarkChoice } from './watermarkChoiceStore';

/**
 * Draws the open choice (design: trial-upsell a2) one time for the whole app
 * (`WatermarkUpsellHost`), and sends its events. The offer opens after the choice closes, so two
 * modals never stack.
 */
const WatermarkChoiceHostComponent: React.FC = () => {
  const request = useSyncExternalStore(subscribeWatermarkChoice, getWatermarkChoice, getWatermarkChoice);
  const plan = useMusic24Plan();
  const isMember = useIsUpsellMember();
  const isNativeEmbed = useIsNativeEmbed();
  const lowestPrice = useLowestPlanPrice();
  const { summary } = useWatermarkSummary();
  const openOffer = useOpenWatermarkOffer();
  const eventFacts = useUpsellEventFacts();
  const surface = request?.surface ?? null;

  // One `upsell_shown` for each opening.
  useEffect(() => {
    if (request !== null) {
      trackUpsellShown(eventFacts(request.surface));
    }
    // Only when a request opens: a new count must not send the event again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  // A page that goes away closes its choice.
  useEffect(() => closeWatermarkChoice, []);

  const remove = useCallback(() => {
    closeWatermarkChoice();
    if (surface !== null) {
      openOffer(surface);
    }
  }, [openOffer, surface]);
  const proceed = useCallback(() => {
    const onContinue = request?.onContinue;
    if (surface !== null) {
      trackUpsellContinued(eventFacts(surface));
    }
    if (request?.downloadHref !== undefined) {
      request.onDownload?.();
      // The click is on the real download link of the modal: the link must live until the browser
      // starts the download, so the modal closes after the click.
      window.setTimeout(closeWatermarkChoice, 0);
      return;
    }
    closeWatermarkChoice();
    onContinue?.();
  }, [eventFacts, request, surface]);
  const dismiss = useCallback(() => {
    if (surface !== null) {
      trackUpsellDismissed(eventFacts(surface));
    }
    closeWatermarkChoice();
  }, [eventFacts, surface]);

  return (
    <WatermarkChoiceModal
      request={request}
      plan={plan}
      isMember={isMember}
      price={isNativeEmbed ? null : lowestPrice}
      files={summary.files}
      cleanCopiesAvailable={summary.cleanCopiesAvailable}
      onRemove={remove}
      onContinue={proceed}
      onDismiss={dismiss}
    />
  );
};

export const WatermarkChoiceHost = TypedMemo(WatermarkChoiceHostComponent);
